import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  TextField,
  Button,
  InputAdornment,
  CircularProgress,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Select,
  MenuItem,
  FormControl,
  SelectChangeEvent,
  Tooltip,
  Stack,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  ContentCopy as CopyIcon,
  Send as SendIcon,
  Visibility as VisibilityIcon,
  Code as CodeIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useMCP } from '../../contexts/MCPContext';
import { EditableEmailSection } from './EditableEmailSection';
import { InPlaceEditableEmailPreview } from './InPlaceEditableEmailPreview';
import { TokenUsage, LLMProvider, AgentMessage } from '../../contexts/MCPContext';
import { EmailSection } from '../../types/email';
import { header } from 'utils/auth';
import { time } from 'console';
import SplitPaneMAIQ from 'app/components/ReusableUIComponents/HighLevelComponents/SplitPaneMAIQ';
import MarkupMAIQ from 'app/components/ReusableUIComponents/HighLevelComponents/MarkupMAIQ';
import { useTheme } from '@mui/material/styles';
import Editor from '@monaco-editor/react';
import ChatIcon from '@mui/icons-material/Chat';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

interface EmailPreviewProps {
  sections: EmailSection[];
}

interface StatusTrayProps {
  tokenUsage: TokenUsage;
  llmProvider: LLMProvider;
  agentMessage?: AgentMessage;
}

// Function to convert sections to HTML
const sectionsToHtml = (sections: EmailSection[]): string => {
  const sectionContent = sections.reduce((acc, section) => {
    const content = section.content || section.defaultContent;
    if (!content || content === 'No content yet') return acc;

    // Store content by section ID
    acc[section.id] = content;
    return acc;
  }, {} as Record<string, string>);

  return `
<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<title>Email Template</title>
<style>
    body {
        font-family: Arial, sans-serif;
        margin: 0;
        padding: 20px;
        background-color: #f4f4f4;
        color: #333;
    }
    .container {
        background-color: #ffffff;
        padding: 2rem;
        border-radius: 8px;
        box-shadow: 0 2px 12px rgba(0,0,0,0.08);
        max-width: 800px;
        width: 100%;
        margin: 2rem auto;
        box-sizing: border-box;
    }
    .es-button-border {
        border-style: solid;
        border-color: #2cb543;
        background: #007cc0;
        border-width: 0px;
        display: inline-block;
        border-radius: 0px;
        width: auto;
    }
    .es-button {
        text-decoration: none;
        color: #FFFFFF;
        font-size: 14px;
        display: inline-block;
        background: #007cc0;
        border-radius: 0px;
        font-family: roboto, 'helvetica neue', helvetica, arial, sans-serif;
        font-weight: bold;
        font-style: normal;
        line-height: 16.8px;
        width: auto;
        text-align: center;
        padding: 10px 20px;
        border: none;
    }
    .cta-buttons {
        display: flex;
        justify-content: space-between;
        margin-top: 20px;
        gap: 10px;
    }
    @media (max-width: 840px) {
        .container {
            margin: 1rem;
            padding: 1.5rem;
        }
    }
    @media (max-width: 600px) {
        .container {
            margin: 0.5rem;
            padding: 1rem;
        }
        .cta-buttons {
            flex-direction: column;
        }
        .es-button {
            width: 100%;
            margin-bottom: 10px;
        }
    }
    h2 {
        color: #2A2A2A;
        font-size: 20px;
    }
    h3 {
        color: #333333;
    }
    ul {
        margin: 0;
        padding: 0;
        list-style-type: none;
    }
    li {
        margin-bottom: 10px;
    }
    .signature {
        margin-top: 20px;
        padding-top: 20px;
        border-top: 1px solid #eee;
    }
    .header-logo, .vehicle-image {
        text-align: center;
        margin-bottom: 20px;
    }
    .section {
        margin-bottom: 20px;
    }
</style>
</head>
<body>
<div class="container">
    <div class="header-logo">
        <img src="https://static.wixstatic.com/media/159cea_91830bdb553e417fb7e5443f0797403d~mv2.png/v1/fill/w_158,h_54,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/MIQ-logo-White-HighRes.png" alt="Dealership Logo">
    </div>
    
    <!-- Greeting -->
    <div class="section">
        ${sectionContent['greeting']?.replace(/\n/g, '<br/>') || ''}
    </div>

    <!-- Dealership Information -->
    <div class="section">
        ${sectionContent['dealership-info']?.replace(/\n/g, '<br/>') || ''}
    </div>

    <!-- Vehicle Details -->
    <div class="section">
        <h3>Vehicle Details:</h3>
        ${sectionContent['vehicle-details']?.replace(/\n/g, '<br/>') || ''}
    </div>

    <!-- Special Offer -->
    ${
      sectionContent['offer-details']
        ? `
    <div class="section">
        <h3>Special Offer:</h3>
        ${sectionContent['offer-details'].replace(/\n/g, '<br/>')}
    </div>
    `
        : ''
    }

    <!-- Maintenance & Services -->
    ${
      sectionContent['maintenance-services']
        ? `
    <div class="section">
        <h3>Maintenance & Services:</h3>
        ${sectionContent['maintenance-services'].replace(/\n/g, '<br/>')}
    </div>
    `
        : ''
    }

    <!-- Call to Action -->
    <div class="section">
        ${sectionContent['call-to-action']?.replace(/\n/g, '<br/>') || ''}
        <div class="cta-buttons">
            <table cellpadding="0" cellspacing="0" width="100%" role="none" style="mso-table-lspace:0pt;mso-table-rspace:0pt;border-collapse:collapse;border-spacing:0px">
                <tr style="border-collapse:collapse">
                    <td align="left" style="padding:20px 0;Margin:0">
                        <!--[if mso]><table style="width:100%" cellpadding="0" cellspacing="0"><tr><td style="width:33%" valign="top"><![endif]-->
                        <table cellpadding="0" cellspacing="0" class="es-left" align="left" role="none" style="mso-table-lspace:0pt;mso-table-rspace:0pt;border-collapse:collapse;border-spacing:0px;float:left;width:33%">
                            <tr style="border-collapse:collapse">
                                <td align="center" style="padding:0;Margin:0">
                                    <span class="es-button-border">
                                        <a href="https://www.altoonahonda.com/used-inventory/index.htm?utm_source=miq&utm_medium=email&utm_campaign=dor" class="es-button" target="_blank">Inventory</a>
                                    </span>
                                </td>
                            </tr>
                        </table>
                        <!--[if mso]></td><td style="width:33%" valign="top"><![endif]-->
                        <table cellpadding="0" cellspacing="0" class="es-left" align="left" role="none" style="mso-table-lspace:0pt;mso-table-rspace:0pt;border-collapse:collapse;border-spacing:0px;float:left;width:33%">
                            <tr style="border-collapse:collapse">
                                <td align="center" style="padding:0;Margin:0">
                                    <span class="es-button-border">
                                        <a href="https://www.altoonahonda.com/carfax-value-your-trade.htm?utm_source=miq&utm_medium=email&utm_campaign=dor" class="es-button" target="_blank">Value Trade</a>
                                    </span>
                                </td>
                            </tr>
                        </table>
                        <!--[if mso]></td><td style="width:33%" valign="top"><![endif]-->
                        <table cellpadding="0" cellspacing="0" class="es-right" align="right" role="none" style="mso-table-lspace:0pt;mso-table-rspace:0pt;border-collapse:collapse;border-spacing:0px;float:right;width:33%">
                            <tr style="border-collapse:collapse">
                                <td align="center" style="padding:0;Margin:0">
                                    <span class="es-button-border">
                                        <a href="https://www.altoonahonda.com/financing/application.htm?utm_source=miq&utm_medium=email&utm_campaign=dor" class="es-button" target="_blank">Apply for Financing</a>
                                    </span>
                                </td>
                            </tr>
                        </table>
                        <!--[if mso]></td></tr></table><![endif]-->
                    </td>
                </tr>
            </table>
        </div>
    </div>

    <!-- Signature -->
    <div class="signature">
        <p>Best regards,</p>
        <p>Sales Team<br>{state.context.selectedDealership.details?.name || 'Dealership'}</p>
    </div>
</div>
</body>
</html>
  `;
};

// Add a styled email preview component
const EmailPreview: React.FC<EmailPreviewProps> = ({ sections }) => {
  const { state, dispatch } = useMCP();

  const handleSectionContentChange = async (
    sectionId: string,
    newContent: string,
  ) => {
    // First update the local state immediately for responsiveness
    dispatch({
      type: 'UPDATE_SECTION_CONTENT',
      payload: {
        sectionId,
        content: newContent,
      },
    });

    // Then trigger email regeneration using dedicated endpoint
    try {
      const response = await fetch(
        'http://localhost:3001/api/email/regenerate',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            sections: state.context.template.structure.sections,
            sectionId,
            newContent,
          }),
        },
      );

      if (!response.ok) {
        throw new Error('Failed to regenerate email');
      }

      const data = await response.json();
      if (data.success && data.updates?.template?.structure) {
        dispatch({
          type: 'UPDATE_TEMPLATE_SECTIONS',
          payload: {
            sections: data.updates.template.structure.sections,
            generatedHtml: data.updates.template.structure.generatedHtml,
          },
        });
      }
    } catch (error) {
      console.error('Error regenerating email:', error);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {sections.map(section => (
        <Paper
          key={section.id}
          elevation={1}
          sx={{
            p: 2,
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            '&:hover': {
              borderColor: 'primary.main',
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Chip
              label={section.name}
              color="primary"
              variant="outlined"
              sx={{ fontWeight: 600, fontSize: '1rem' }}
            />
            {section.required && (
              <Typography component="span" color="error" sx={{ ml: 1 }}>
                *
              </Typography>
            )}
          </Box>
          <EditableEmailSection
            section={section}
            onContentChange={handleSectionContentChange}
          />
        </Paper>
      ))}
    </Box>
  );
};

const StatusTray: React.FC<StatusTrayProps> = ({
  tokenUsage,
  llmProvider,
  agentMessage,
}) => {
  const { dispatch } = useMCP();
  const formatCost = (cost: number | undefined | null) => {
    if (typeof cost !== 'number' || isNaN(cost)) return '$0.0000';
    return `$${cost.toFixed(4)}`;
  };

  const handleModelChange = (event: SelectChangeEvent<LLMProvider>) => {
    dispatch({
      type: 'UPDATE_LLM_PROVIDER',
      payload: event.target.value as LLMProvider,
    });
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 0,
        left: '70px',
        right: 0,
        backgroundColor: 'background.paper',
        borderTop: 1,
        borderColor: 'divider',
        p: 1,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        zIndex: 1000,
        boxShadow: '0 -2px 12px rgba(0,0,0,0.08)',
      }}
    >
      <Box sx={{ display: 'flex', gap: 1 }}>
        <FormControl size="small">
          <Select
            value={llmProvider}
            onChange={handleModelChange}
            displayEmpty
            variant="outlined"
            size="small"
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.main',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.main',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.main',
              },
              '& .MuiSelect-select': {
                py: 0.5,
                px: 1,
                fontSize: '0.8125rem',
                color: 'primary.main',
              },
              '& .MuiMenuItem-root': {
                fontSize: '0.8125rem',
              },
              minWidth: '120px',
              height: '24px',
              backgroundColor: 'background.paper',
            }}
          >
            <MenuItem value={LLMProvider.OPENAI}>OpenAI</MenuItem>
            <MenuItem value={LLMProvider.ANTHROPIC}>Anthropic</MenuItem>
          </Select>
        </FormControl>
        <Chip
          label={`Tokens: ${tokenUsage?.totalTokens ?? 0}`}
          size="small"
          color="primary"
          variant="outlined"
        />
        <Chip
          label={`Prompt Cost: ${formatCost(tokenUsage?.promptCost)}`}
          size="small"
          color="secondary"
          variant="outlined"
        />
        <Chip
          label={`Completion Cost: ${formatCost(tokenUsage?.completionCost)}`}
          size="small"
          color="secondary"
          variant="outlined"
        />
        <Chip
          label={`Total Cost: ${formatCost(tokenUsage?.cost)}`}
          size="small"
          color="secondary"
          variant="outlined"
        />
        {agentMessage && (
          <Chip
            label={`Agent: ${agentMessage.message} took ${agentMessage.execution_time_ms}ms`}
            size="small"
            color="info"
            variant="outlined"
            sx={{
              maxWidth: '600px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          />
        )}
      </Box>
    </Box>
  );
};

interface MiaChatProps {
  onReset: () => void;
}

export const MiaChat: React.FC<MiaChatProps> = ({ onReset }) => {
  const theme = useTheme();
  const {
    state,
    sendMessage,
    addMessage,
    resetMessages,
    dispatch,
    addInitialGreeting,
  } = useMCP();
  const [currentInput, setCurrentInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailGenerating, setIsEmailGenerating] = useState(false);
  const [previewMode, setPreviewMode] = useState<'preview' | 'html'>('preview');
  const [emailGenerationStarted, setEmailGenerationStarted] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const dealership = state.dealership;
  const boxRef = useRef<HTMLPreElement>(null);
  const emailPreviewContainerRef = useRef<HTMLDivElement>(null);
  const codePanelRef = useRef<HTMLDivElement>(null);
  const shouldAutoScrollRef = useRef(true);
  const monacoEditorRef = useRef<any>(null);
  const [userScrolledUp, setUserScrolledUp] = useState(false);
  const [copied, setCopied] = useState(false);
  const [monacoLoaded, setMonacoLoaded] = useState(false);
  const [isMonacoLoading, setIsMonacoLoading] = useState(false);
  const [suggestionsVisible, setSuggestionsVisible] = useState(true);

  //when state for preview mode changes we need to react to that state change
  useEffect(() => {
    const nextMode =
      state.preview_mode === 'preview' || state.preview_mode === 'html'
        ? state.preview_mode
        : 'preview';

    setPreviewMode(prev => (prev !== nextMode ? nextMode : prev));
  }, [state.preview_mode]);

  // Track email generation state from streaming responses
  useEffect(() => {
    if (state.preview_mode === 'html') {
      setIsEmailGenerating(true);
      setEmailGenerationStarted(true);
    } else if (state.preview_mode === 'preview') {
      // Add a small delay to ensure the email is fully generated before enabling controls
      setTimeout(() => {
        setIsEmailGenerating(false);
      }, 500);
    }
  }, [state.preview_mode]);

  // Auto-scroll email preview to bottom function
  const scrollEmailPreviewToBottom = useCallback(() => {
    const emailContainer = emailPreviewContainerRef.current;
    if (emailContainer) {
      emailContainer.scrollTop = emailContainer.scrollHeight;
    }

    // For HTML mode
    const htmlEl = boxRef.current;
    if (htmlEl) {
      htmlEl.scrollTop = htmlEl.scrollHeight;
    }
  }, []);

  useEffect(() => {
    scrollEmailPreviewToBottom();
  }, [
    state.context.template.structure.generatedHtml,
    scrollEmailPreviewToBottom,
  ]); // Auto-scroll when content changes

  // Auto-scroll when preview mode changes
  useEffect(() => {
    const timer = setTimeout(() => {
      scrollEmailPreviewToBottom();
    }, 200); // Delay to ensure mode switch is complete
    return () => clearTimeout(timer);
  }, [previewMode, scrollEmailPreviewToBottom]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Focus input when component mounts and after messages change
  useEffect(() => {
    scrollToBottom();
    // Small delay to ensure the component is fully rendered
    const timer = setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [state.messages]);

  const handleReset = useCallback(() => {
    onReset();
  }, [onReset]);

  const handleSendMessage = useCallback(
    async (content: string) => {
      if (!content.trim()) return;
      setIsLoading(true);
      setSuggestionsVisible(false); // Hide suggestions after sending
      try {
        await sendMessage(content);
        setCurrentInput('');
      } catch (error) {
        console.error('Error details:', error);
        addMessage(
          'assistant',
          'Sorry, there was an error processing your request. Please try again.',
        );
      } finally {
        setIsLoading(false);
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }
    },
    [sendMessage, addMessage],
  );

  // Show suggestions again when a new assistant message arrives
  useEffect(() => {
    const lastMsg = state.messages[state.messages.length - 1];
    // Only show suggestions if last message is main assistant reply (no bgcolor)
    if (lastMsg && lastMsg.role === 'assistant' && !lastMsg.bgcolor) {
      setSuggestionsVisible(true);
    } else {
      setSuggestionsVisible(false);
    }
  }, [state.messages]);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(currentInput);
    }
  };

  const handleSubmit = () => {
    handleSendMessage(currentInput);
  };

  const handleCopyEmail = async () => {
    try {
      // Always copy HTML content regardless of preview mode
      const htmlContent =
        state.context.template.structure.generatedHtml ||
        sectionsToHtml(state.context.template.structure.sections);
      await navigator.clipboard.writeText(htmlContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handlePreviewModeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newMode: 'preview' | 'html',
  ) => {
    if (newMode !== null && !isEmailGenerating) {
      setPreviewMode(newMode);
    }
  };

  const generatedHtml =
    state.context.template.structure.generatedHtml &&
    state.context.template.structure.generatedHtml.trim() !== '';
  const showInstructions =
    !emailGenerationStarted && !isEmailGenerating && !generatedHtml;
  const showPreviewPanel =
    emailGenerationStarted || isEmailGenerating || generatedHtml;

  // Auto-scroll code panel to bottom on code update, only if shouldAutoScrollRef.current is true
  useEffect(() => {
    if (!showPreviewPanel || previewMode !== 'html') return;
    if (!monacoEditorRef.current) return;
    if (shouldAutoScrollRef.current) {
      const editor = monacoEditorRef.current;
      const model = editor.getModel();
      if (model) {
        const lineCount = model.getLineCount();
        editor.revealLineInCenter(lineCount);
        editor.setPosition({ lineNumber: lineCount, column: 1 });
      }
    }
  }, [
    state.context.template.structure.generatedHtml,
    showPreviewPanel,
    previewMode,
  ]);

  // Track user scroll position in code panel
  const handleCodePanelScroll = () => {
    const el = codePanelRef.current;
    if (!el) return;
    // If user is within 20px of the bottom, consider as at bottom
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 20;
    shouldAutoScrollRef.current = atBottom;
  };

  // Handle Monaco Editor scroll
  const handleMonacoScroll = () => {
    if (!monacoEditorRef.current) return;
    const editor = monacoEditorRef.current;
    const scrollTop = editor.getScrollTop();
    const scrollHeight = editor.getScrollHeight();
    const clientHeight = editor.getLayoutInfo().height;
    const atBottom = scrollHeight - scrollTop - clientHeight < 50;
    shouldAutoScrollRef.current = atBottom;
  };

  // Load Monaco Editor immediately when component mounts
  useEffect(() => {
    // Monaco Editor will load automatically when the component renders
  }, []);

  // Suggestion logic based on last assistant message
  const getSuggestions = () => {
    const lastAssistantMsg = [...state.messages]
      .reverse()
      .find(m => m.role === 'assistant' && !m.bgcolor); // Main assistant reply only
    if (!lastAssistantMsg) return [];
    const msg = lastAssistantMsg.content.toLowerCase();
    // Example logic (customize as needed)
    if (msg.includes('which one would you like to start')) {
      return [
        'Generate vehicle highlight email',
        'Generate lease offer email',
        'Generate finance offer email',
      ];
    }
    if (msg.match(/themes? like (reliability|maintenance|value)/)) {
      return ['Reliability', 'Maintenance', 'Value'];
    }
    if (msg.match(/vin|vehicle number|enter.*vin|provide.*vin|please.*vin/)) {
      return [];
    }
    if (
      msg.includes('looks good') ||
      msg.includes('is this okay') ||
      msg.includes('are you happy') ||
      msg.includes('let me know if this looks good') ||
      msg.includes('Let me know if this looks good') ||
      msg.includes('Here’s the opening message') ||
      msg.includes('do you approve') ||
      msg.includes('is this fine') ||
      msg.includes('is this correct') ||
      msg.includes('is this what you wanted') ||
      msg.includes('is this what you had in mind') ||
      msg.includes('is this acceptable') ||
      msg.includes('please confirm') ||
      msg.includes('do you want to proceed') ||
      msg.includes('ready to send')
    ) {
      return [
        'Looks good',
        'Regenerate email',
        'Try different wording',
        'Make some changes',
      ];
    }
    if (msg.includes('email generated') || msg.includes('here is your email')) {
      return ['Looks good', 'Regenerate email', 'Edit email'];
    }
    if (
      msg.includes('error in processing your request') ||
      msg.includes('sorry for the confusion') ||
      msg.includes('please hold on for a moment')
    ) {
      return ['Try again'];
    }
    // Default suggestions
    return [];
  };

  const suggestions = getSuggestions();

  // Handle suggestion click
  const handleSuggestionClick = (suggestion: string) => {
    setCurrentInput(suggestion);
    setTimeout(() => {
      handleSendMessage(suggestion);
    }, 0);
  };

  // Chat panel JSX
  const chatPanel = (
    <Paper
      elevation={0}
      sx={{
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        // p: 2.5,
        gap: 2,
        height: '100%',
        overflow: 'hidden',
        borderRadius: 1.5,
        backgroundColor: 'background.paper',
        boxShadow: '0 2px 8px rgba(15, 40, 76, 0.05)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          // mb: 1,
          backgroundColor: 'secondary.main',
          borderRadius: 1,
          px: 2,
          py: 1,
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, color: 'common.white', letterSpacing: 1 }}
        >
          Chat
        </Typography>
        <IconButton
          onClick={handleReset}
          color="secondary"
          sx={{
            background: 'transparent',
            borderRadius: 1,
            transition: 'background 0.2s',
            '&:hover': { background: 'action.hover' },
          }}
        >
          <Tooltip title="Reset Chat" arrow>
            <RefreshIcon sx={{ color: 'common.white' }} />
          </Tooltip>
        </IconButton>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          minHeight: 0,
          px: 1,
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(0,0,0,0.05)',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(0,0,0,0.3)',
            borderRadius: '4px',
            '&:hover': {
              background: 'rgba(0,0,0,0.5)',
            },
          },
        }}
      >
        <List sx={{ pb: 2 }}>
          {state.messages
            .filter(message => message.role !== 'system')
            .map((message, index, arr) => {
              const isUser = message.role === 'user';
              const prev = arr[index - 1];
              const isSameAuthorAsPrev = prev && prev.role === message.role;
              const backgroundColor = message.bgcolor
                ? message.bgcolor
                : isUser
                ? 'secondary.main'
                : 'grey.100';
              return (
                <ListItem
                  key={index}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: isUser ? 'flex-end' : 'flex-start',
                    mb: isSameAuthorAsPrev ? 0.5 : 2,
                    background: 'none',
                    px: 0,
                  }}
                >
                  <Box
                    sx={{
                      p: 1.2,
                      px: 2,
                      maxWidth: '85%',
                      backgroundColor: backgroundColor,
                      color: isUser ? 'common.white' : 'text.primary',
                      borderRadius: isUser
                        ? '18px 18px 4px 18px'
                        : '18px 18px 18px 4px',
                      ml: isUser ? 'auto' : 0,
                      mr: !isUser ? 'auto' : 0,
                      boxShadow: 'none',
                      border: '1px solid',
                      borderColor: isUser ? 'secondary.light' : 'divider',
                      transition: 'background 0.2s',
                      fontSize: '0.95rem',
                      lineHeight: 1.4,
                      '& *': {
                        fontSize: 'inherit !important',
                        lineHeight: 'inherit !important',
                      },
                      '& h1, & h2, & h3, & h4, & h5, & h6': {
                        fontSize: '1.1rem !important',
                        fontWeight: 600,
                        margin: '0.5rem 0',
                      },
                      '& p': {
                        margin: '0.25rem 0',
                      },
                      '& code': {
                        fontSize: '0.9rem !important',
                      },
                      '& pre': {
                        fontSize: '0.85rem !important',
                      },
                    }}
                  >
                    <MarkupMAIQ content={message.content} />
                  </Box>
                </ListItem>
              );
            })}
          {isLoading && (
            <ListItem sx={{ justifyContent: 'center' }}>
              <CircularProgress size={24} color="secondary" />
            </ListItem>
          )}
          <div ref={messagesEndRef} />
        </List>
      </Box>

      <Box sx={{ mt: 'auto' }}>
        {/* Suggestion Chips */}
        {suggestionsVisible && suggestions.length > 0 && (
          <Box sx={{ mb: 1 }}>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontWeight: 500,
                mb: 0.5,
                display: 'block',
              }}
            >
              Suggestions (click or type):
            </Typography>
            <Box
              sx={{
                overflowX: 'auto',
                whiteSpace: 'nowrap',
                width: '100%',
                pb: 0.5,
                mb: 0.5,
                '::-webkit-scrollbar': { height: 6 },
                '::-webkit-scrollbar-thumb': {
                  background: '#eee',
                  borderRadius: 3,
                },
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                sx={{ flexWrap: 'nowrap', width: 'max-content' }}
              >
                {suggestions.map((s, i) => (
                  <Chip
                    key={s + i}
                    label={s}
                    color="primary"
                    variant="outlined"
                    clickable
                    onClick={() => handleSuggestionClick(s)}
                    sx={{ fontWeight: 500, fontSize: '0.85rem', height: 28 }}
                  />
                ))}
              </Stack>
            </Box>
          </Box>
        )}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            backgroundColor: 'background.default',
            // border: '1.5px solid',
            // px: 2,
            py: 1,
            // mt: 1.5,
            boxShadow: 'none',
          }}
        >
          <TextField
            fullWidth
            multiline
            minRows={1}
            maxRows={6}
            value={currentInput}
            onChange={e => setCurrentInput(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isLoading}
            placeholder="Ask Mia anything..."
            variant="standard"
            inputRef={inputRef}
            InputProps={{
              disableUnderline: true,
              sx: {
                fontSize: '1rem',
                background: 'transparent',
                borderColor: 'divider',
                borderRadius: '24px',
                px: 0,
                py: 0,
                '& .MuiInputBase-input': {
                  borderColor: 'divider',
                  borderRadius: '24px',
                  fontSize: '1rem',
                  padding: 0.1,
                },
              },
            }}
            sx={{
              background: 'transparent',
              borderRadius: '24px',
              fontSize: '1rem',
              width: '100%',
              px: 1,
              py: 1,
              boxShadow: 'none',
              border: '1.5px solid',
              borderColor: 'divider',
              outline: 'none',
              '& textarea': {
                resize: 'none',
                overflow: 'auto',
              },
            }}
          />
          <Button
            variant="contained"
            color="secondary"
            onClick={handleSubmit}
            disabled={isLoading || !currentInput.trim()}
            sx={{
              width: '40px',
              height: '40px',
              minWidth: '40px',
              minHeight: '40px',
              maxWidth: '40px',
              maxHeight: '40px',
              p: 0,
              borderRadius: '50%',
              boxShadow: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'secondary.main',
              ml: 1,
              '&:hover': {
                backgroundColor: 'secondary.dark',
                boxShadow: '0 2px 8px rgba(44,181,67,0.10)',
              },
              '&:active': {
                backgroundColor: 'secondary.dark',
                boxShadow: '0 1px 4px rgba(44,181,67,0.15)',
              },
              transition: 'background 0.2s, box-shadow 0.2s',
            }}
            aria-label="Send"
          >
            <SendIcon sx={{ fontSize: 22 }} />
          </Button>
        </Box>
      </Box>
    </Paper>
  );

  // Email preview panel JSX
  const emailPanel = (
    <Paper
      elevation={0}
      sx={{
        width: '96.5%',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        height: '100%',
        minHeight: 0,
        flex: 1,
        overflow: 'hidden',
        borderRadius: 1.5,
        backgroundColor: 'background.paper',
        boxShadow: '0 2px 8px rgba(15, 40, 76, 0.05)',
      }}
    >
      {showPreviewPanel && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: 'secondary.main',
            borderRadius: 1,
            px: 2,
            py: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: 'common.white', letterSpacing: 1 }}
            >
              Email Preview
            </Typography>
            {previewMode === 'preview' && (
              <Typography
                variant="caption"
                sx={{
                  color: 'common.white',
                  opacity: 0.9,
                  fontSize: '0.7rem',
                  fontWeight: 400,
                }}
              >
                💡 Click on any email content to edit it
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isEmailGenerating && (
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 1 }}
              >
                <CircularProgress size={16} sx={{ color: 'common.white' }} />
                <Typography
                  variant="caption"
                  sx={{
                    color: 'common.white',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                  }}
                >
                  Generating...
                </Typography>
              </Box>
            )}
            <ToggleButtonGroup
              value={previewMode}
              exclusive
              onChange={handlePreviewModeChange}
              size="small"
              disabled={isEmailGenerating}
              sx={{
                backgroundColor: 'transparent',
                borderRadius: 2,
                boxShadow: 'none',
                height: 40,
                opacity: isEmailGenerating ? 0.6 : 1,
                pointerEvents: isEmailGenerating ? 'none' : 'auto',
              }}
            >
              <Tooltip title="Preview" arrow>
                <ToggleButton
                  value="preview"
                  disabled={isEmailGenerating}
                  sx={{
                    minWidth: 40,
                    width: 40,
                    height: 40,
                    borderRadius: '8px 0 0 8px',
                    background: 'none',
                    border: 'none',
                    boxShadow: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color:
                      previewMode === 'preview'
                        ? `${theme.palette.primary.main} !important`
                        : `${theme.palette.common.white} !important`,
                    transition: 'background 0.2s, color 0.2s',
                    '&:hover': {
                      backgroundColor: 'rgba(15,40,76,0.85)',
                      color: 'common.white !important',
                    },
                    '&:disabled': {
                      color: `${theme.palette.common.white} !important`,
                      opacity: 0.6,
                    },
                    px: 0,
                  }}
                >
                  <VisibilityIcon sx={{ fontSize: 22 }} />
                </ToggleButton>
              </Tooltip>
              <Tooltip title="HTML Code" arrow>
                <ToggleButton
                  value="html"
                  disabled={isEmailGenerating}
                  sx={{
                    minWidth: 40,
                    width: 40,
                    height: 40,
                    borderRadius: '0 8px 8px 0',
                    background: 'none',
                    border: 'none',
                    boxShadow: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color:
                      previewMode === 'html'
                        ? `${theme.palette.primary.main} !important`
                        : `${theme.palette.common.white} !important`,
                    transition: 'background 0.2s, color 0.2s',
                    '&:hover': {
                      backgroundColor: 'rgba(15,40,76,0.85)',
                      color: 'common.white !important',
                    },
                    '&:disabled': {
                      color: `${theme.palette.common.white} !important`,
                      opacity: 0.6,
                    },
                    px: 0,
                  }}
                >
                  <CodeIcon sx={{ fontSize: 22 }} />
                </ToggleButton>
              </Tooltip>
            </ToggleButtonGroup>
            <IconButton
              onClick={handleCopyEmail}
              color="primary"
              sx={{
                ml: 1,
                background: 'transparent',
                borderRadius: 1,
                transition: 'background 0.2s',
                '&:hover': { background: 'action.hover' },
                opacity: !generatedHtml || isEmailGenerating ? 0.5 : 1,
                pointerEvents:
                  !generatedHtml || isEmailGenerating ? 'none' : 'auto',
              }}
              disabled={!generatedHtml || isEmailGenerating}
            >
              <Tooltip title={copied ? 'Copied!' : 'Copy HTML'} arrow>
                {copied ? <CheckCircleIcon color="success" /> : <CopyIcon />}
              </Tooltip>
            </IconButton>
          </Box>
        </Box>
      )}
      <Box
        ref={emailPreviewContainerRef}
        sx={{
          flex: 1,
          minHeight: 0,
          height: '100%',
          overflowY: 'auto',
          overflowX: 'hidden',
          pr: 1,
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(0,0,0,0.05)',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(0,0,0,0.3)',
            borderRadius: '4px',
            '&:hover': {
              background: 'rgba(0,0,0,0.5)',
            },
          },
        }}
      >
        {showInstructions && (
          <Box
            sx={{
              width: '100%',
              minHeight: '100%',
              bgcolor: 'grey.100',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              py: 0,
            }}
          >
            <Box
              sx={{
                width: '100%',
                maxWidth: 520,
                bgcolor: 'grey.100',
                borderRadius: 2,
                boxShadow: 0,
                border: '1px solid',
                borderColor: 'grey.200',
                p: { xs: 1.5, md: 2.5 },
                mx: 'auto',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: 'primary.main',
                  mb: 1.2,
                  textAlign: 'center',
                  fontSize: '1.15rem',
                }}
              >
                Welcome to MiaChat (AI Email Generator)
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  mb: 2,
                  textAlign: 'center',
                  maxWidth: 420,
                  fontSize: '0.95rem',
                }}
              >
                Get started by chatting with Mia and generate beautiful,
                editable emails for your dealership. Here's how you can use all
                the features:
              </Typography>
              <Stack spacing={2} sx={{ width: '100%' }}>
                {[
                  {
                    icon: <ChatIcon fontSize="small" color="primary" />,
                    title: 'Chat with Mia',
                    desc: 'Type your request or select a suggestion to start generating an email.',
                  },
                  {
                    icon: (
                      <TipsAndUpdatesIcon fontSize="small" color="secondary" />
                    ),
                    title: 'Use Suggestions',
                    desc: 'Click on the suggestion chips above the input to quickly reply or try common actions.',
                  },
                  {
                    icon: (
                      <EditIcon
                        fontSize="small"
                        sx={{ color: 'primary.main' }}
                      />
                    ),
                    title: 'Edit Email Preview',
                    desc: 'Click on any part of the email preview to edit the content directly.',
                  },
                  {
                    icon: (
                      <CodeIcon
                        fontSize="small"
                        sx={{ color: 'secondary.main' }}
                      />
                    ),
                    title: 'HTML Code View & Edit',
                    desc: 'Switch to the HTML view to see and edit the raw email code.',
                  },
                  {
                    icon: (
                      <ContentCopyIcon
                        fontSize="small"
                        sx={{ color: 'primary.main' }}
                      />
                    ),
                    title: 'Copy Email Code',
                    desc: 'Once the email is generated, use the copy icon to copy the HTML code.',
                  },
                  {
                    icon: (
                      <RefreshIcon
                        fontSize="small"
                        sx={{ color: 'secondary.main' }}
                      />
                    ),
                    title: 'Regenerate or Try Again',
                    desc: 'Use the regenerate or try again options to get a new version of the email.',
                  },
                ].map((step, idx) => (
                  <Box
                    key={step.title}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      width: '100%',
                    }}
                  >
                    <Box
                      sx={{
                        minWidth: 28,
                        minHeight: 28,
                        width: 28,
                        height: 28,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'grey.200',
                        borderRadius: 2,
                        boxShadow: 0,
                        flexShrink: 0,
                      }}
                    >
                      {step.icon}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          color:
                            idx === 0
                              ? 'primary.main'
                              : idx === 1
                              ? 'secondary.main'
                              : 'text.primary',
                          mb: 0.2,
                          fontSize: '0.98rem',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {step.title}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          lineHeight: 1.4,
                          fontSize: '0.91rem',
                          wordBreak: 'break-word',
                        }}
                      >
                        {step.desc}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Stack>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  mt: 2.5,
                  textAlign: 'center',
                  display: 'block',
                  fontSize: '0.91rem',
                }}
              >
                Need help? Just start typing or select a suggestion to begin!
              </Typography>
            </Box>
          </Box>
        )}
        {showPreviewPanel &&
          (previewMode === 'preview' ? (
            <Box
              sx={{
                height: '100%',
                width: '100%',
                position: 'relative',
                minHeight: 0,
              }}
            >
              {isEmailGenerating && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10,
                    borderRadius: 1,
                  }}
                >
                  <Box sx={{ textAlign: 'center' }}>
                    <CircularProgress size={40} color="primary" />
                    <Typography
                      variant="body2"
                      sx={{ mt: 1, color: 'text.secondary' }}
                    >
                      Generating email...
                    </Typography>
                  </Box>
                </Box>
              )}
              <InPlaceEditableEmailPreview
                htmlContent={
                  state.context.template.structure.generatedHtml || ''
                }
                sections={state.context.template.structure.sections || []}
                onContentChange={async (
                  sectionId: string,
                  newContent: string,
                ) => {
                  // First update the local state immediately for responsiveness
                  dispatch({
                    type: 'UPDATE_SECTION_CONTENT',
                    payload: {
                      sectionId,
                      content: newContent,
                    },
                  });

                  // Then trigger email regeneration using dedicated endpoint
                  try {
                    const response = await fetch(
                      'http://localhost:3001/api/email/regenerate',
                      {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                          sections: state.context.template.structure.sections,
                          sectionId,
                          newContent,
                        }),
                      },
                    );

                    if (!response.ok) {
                      throw new Error('Failed to regenerate email');
                    }

                    const data = await response.json();
                    if (data.success && data.updates?.template?.structure) {
                      dispatch({
                        type: 'UPDATE_TEMPLATE_SECTIONS',
                        payload: {
                          sections: data.updates.template.structure.sections,
                          generatedHtml:
                            data.updates.template.structure.generatedHtml,
                        },
                      });
                    }
                  } catch (error) {
                    console.error('Error regenerating email:', error);
                  }
                }}
                onHtmlChange={(newHtml: string) => {
                  // Update the HTML immediately in the local state
                  dispatch({
                    type: 'UPDATE_TEMPLATE_SECTIONS',
                    payload: {
                      sections: state.context.template.structure.sections,
                      generatedHtml: newHtml,
                    },
                  });
                }}
              />
            </Box>
          ) : (
            <Box
              ref={codePanelRef}
              onScroll={handleCodePanelScroll}
              sx={{
                flex: 1,
                minHeight: 0,
                maxHeight: '74vh',
                height: '100%',
                width: '100%',
                overflowY: 'auto',
                overflowX: 'auto',
                background: '#282c34',
                borderRadius: 2,
                display: 'flex',
                alignItems: 'stretch',
                justifyContent: 'stretch',
              }}
            >
              <Editor
                height="600px"
                defaultLanguage="html"
                value={state.context.template.structure.generatedHtml || ''}
                onChange={value => {
                  if (value !== undefined) {
                    dispatch({
                      type: 'UPDATE_TEMPLATE_SECTIONS',
                      payload: {
                        sections: state.context.template.structure.sections,
                        generatedHtml: value,
                      },
                    });
                  }
                }}
                onMount={editor => {
                  monacoEditorRef.current = editor;
                  editor.onDidScrollChange(handleMonacoScroll);
                }}
                theme="vs-dark"
                options={{
                  minimap: { enabled: false },
                  fontSize: 16,
                  lineNumbers: 'on',
                  roundedSelection: false,
                  scrollBeyondLastLine: false,
                  automaticLayout: true,
                  wordWrap: 'on',
                  folding: true,
                  lineDecorationsWidth: 10,
                  lineNumbersMinChars: 3,
                  glyphMargin: false,
                  padding: { top: 16, bottom: 16 },
                }}
              />
            </Box>
          ))}
      </Box>
    </Paper>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '85vh',
        width: '100%',
        position: 'relative',
        backgroundColor: 'background.default',
      }}
    >
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          p: { xs: 1, md: 1 },
          gap: 3,
          overflow: 'hidden',
        }}
      >
        <SplitPaneMAIQ left={chatPanel} right={emailPanel} />
      </Box>
      {/* <StatusTray
        tokenUsage={state.tokenUsage}
        llmProvider={state.llmProvider}
        agentMessage={state.agentMessage}
      /> */}
    </Box>
  );
};
